import * as d3 from 'd3';
import React from 'react';

import {XScale} from 'src/components/D3Axis';
import {Site} from 'src/lib/api/project';
import {
  ApiForecastTimeSeries,
  ApiObservationTimeSeries,
  ForecastTimeSeriesQuery,
  ObservationTimeSeriesQuery,
} from 'src/lib/api/series';
import {Dims, Margin} from 'src/lib/commonTypes';
import {getGraphDims} from 'src/lib/vizUtils';

import {VizConfig} from './config';
import {VizVisibilityProvider} from './VisibilityContext';
import VizErrorBoundary from './VizErrorBoundary';
import VizLoading from './VizLoading';
import VizMoreOption from './VizMoreOption';

export const MARGIN = {
  top: 10,
  right: 60,
  bottom: 20,
  left: 60,
};

type Props = {
  section: string;
  title: string;
  renderOptions?: () => React.ReactNode;
  site: Site;
  config: VizConfig;
  observationQueries: ObservationTimeSeriesQuery[];
  observationTimeSeriesList: ApiObservationTimeSeries[];
  forecastQueries: ForecastTimeSeriesQuery[];
  forecastTimeSeriesList: ApiForecastTimeSeries[];
  loadingStateDims: Dims;
  dateRange: [Date, Date];
  setDateRange?: (dateRange: [Date, Date]) => void;
  isLoading: boolean;
  thresholdLinesVisible?: boolean;
  onToggleThresholdLines?: () => void;
  children: (props: Props & {x: XScale; margin: Margin}) => React.ReactNode;
};

/**
 * Wrapper component for groups of time series vizualizations.
 * Handles loading state, error handling, and rendering options.
 */
export default function Viz(props: Props) {
  const {
    section,
    title,
    renderOptions,
    site,
    dateRange,
    observationQueries,
    observationTimeSeriesList,
    forecastQueries,
    forecastTimeSeriesList,
    loadingStateDims,
    isLoading,
    thresholdLinesVisible,
    onToggleThresholdLines,
    children,
  } = props;
  const [vizEl, setVizEl] = React.useState<HTMLDivElement | null>(null);

  const graphDims = getGraphDims(loadingStateDims, MARGIN);

  const x = d3.scaleTime().domain([dateRange[0], dateRange[1]]).range([0, graphDims[0]]);

  /** Concatenate section and vizualization titles (e.g., Short-term, flow). */
  const fullTitle = [section, title.toLowerCase()].filter((x) => !!x).join(', ');

  return (
    <VizVisibilityProvider>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between flex-wrap gap-6">
          <h3 className="text-lg font-semibold">{title}</h3>
          <div className="flex items-center flex-wrap gap-2">
            {renderOptions?.()}
            <VizMoreOption
              title={fullTitle}
              site={site}
              observationQueries={observationQueries}
              observationTimeSeriesList={observationTimeSeriesList}
              forecastQueries={forecastQueries}
              forecastTimeSeriesList={forecastTimeSeriesList}
              vizEl={vizEl}
              thresholdLinesVisible={thresholdLinesVisible}
              onToggleThresholdLines={onToggleThresholdLines}
            />
          </div>
        </div>
        <div ref={setVizEl} className="bg-white">
          {isLoading ? (
            <VizLoading dims={loadingStateDims} margin={MARGIN} />
          ) : (
            <VizErrorBoundary
              dims={loadingStateDims}
              margin={MARGIN}
              context={{dateRange: dateRange.map((d) => d.toString())}}
            >
              <div className="relative viz">{children({...props, x, margin: MARGIN})}</div>
            </VizErrorBoundary>
          )}
        </div>
      </div>
    </VizVisibilityProvider>
  );
}
